// 族譜樹的版面：純函式，沒有 DOM，node 裡也能跑（scripts/check-lineage-layout.mjs 拿它量每棵樹的寬高）。
//
// 由上往下：根在最上面，每一代往下一列（像寶可夢祖先圖、像族譜的「世」）。
// 選這個方向而不是由左往右，是因為資料裡最深的鏈有 15 代：往下 15 列可以捲，往右 15 欄不能。
//
// 大扇形折成格：一個父節點底下的「葉子」超過 FAN 件時，不逐張橫排（shader template 底下 29 張葉子會拉到 4,000px 寬），
// 而是收成一個 COLS 欄的格子，當成一個群組掛在父節點下。有自己子樹的孩子仍然逐棵排。
export const SIZES = {
  CW: 132,   // 卡片寬
  CH: 150,   // 卡片高（縮圖 92 + 標題兩行 + 日期，貼齊不留底）
  GX: 18,    // 同代卡片間距
  GY: 52,    // 代與代之間（連接線的長度）
  GYS: 12,   // 格子內列距
  PAD: 10,   // 格子的內距
  LABEL: 22, // 格子上方的標籤高
  FAN: 3,    // 葉子超過這個數就折成格
  COLS: 5,   // 格子欄數
  TREE_GAP: 44, // 森林裡樹與樹之間
};

const isBranch = (n) => Array.isArray(n.c) && n.c.length > 0;

function measure(n, S) {
  const kids = n.c || [];
  const branches = kids.filter(isBranch);
  const leaves = kids.filter(k => !isBranch(k));
  let items;
  if (leaves.length > S.FAN) {
    const cols = Math.min(S.COLS, leaves.length), rows = Math.ceil(leaves.length / cols);
    items = [
      ...branches.map(b => measure(b, S)),
      { grid: true, leaves, cols, rows,
        w: cols * S.CW + (cols - 1) * S.GX + 2 * S.PAD,
        h: rows * S.CH + (rows - 1) * S.GYS + 2 * S.PAD + S.LABEL },
    ];
  } else {
    items = kids.map(k => measure(k, S));
  }
  const m = { node: n, items };
  if (!items.length) { m.w = S.CW; m.h = S.CH; return m; }
  m.w = Math.max(S.CW, items.reduce((a, i) => a + i.w, 0) + (items.length - 1) * S.GX);
  m.h = S.CH + S.GY + Math.max(...items.map(i => i.h));
  return m;
}

function place(m, x0, y, S, out) {
  const { node, items } = m;
  if (!items.length) {
    m.x = x0; m.y = y;
    out.cards.push({ n: node, x: x0, y });
    return;
  }
  let cx = x0;
  const centers = [];
  for (const it of items) {
    if (it.grid) {
      it.x = cx; it.y = y + S.CH + S.GY;
      const kinds = new Set(it.leaves.map(l => l.k));
      out.groups.push({ x: it.x, y: it.y, w: it.w, h: it.h, n: it.leaves.length, kind: kinds.size === 1 ? [...kinds][0] : 'mixed', parent: node.id });
      it.leaves.forEach((leaf, i) => {
        const col = i % it.cols, row = Math.floor(i / it.cols);
        out.cards.push({ n: leaf, x: it.x + S.PAD + col * (S.CW + S.GX), y: it.y + S.LABEL + S.PAD + row * (S.CH + S.GYS), inGrid: true });
      });
      centers.push(it.x + it.w / 2);
    } else {
      place(it, cx, y + S.CH + S.GY, S, out);
      centers.push(it.x + S.CW / 2);
    }
    cx += it.w + S.GX;
  }
  // 父節點放在第一個與最後一個孩子的正中間，但不跑出自己子樹的範圍
  const mid = (centers[0] + centers[centers.length - 1]) / 2 - S.CW / 2;
  m.x = Math.min(Math.max(mid, x0), x0 + m.w - S.CW);
  m.y = y;
  out.cards.push({ n: node, x: m.x, y });
  const px = m.x + S.CW / 2, py = y + S.CH;
  for (const it of items) {
    const tx = it.grid ? it.x + it.w / 2 : it.x + S.CW / 2;
    const ty = it.grid ? it.y : it.y;
    const child = it.grid ? null : it.node;
    out.edges.push({ x1: px, y1: py, x2: tx, y2: ty, kind: it.grid ? 'group' : (child.k || 'fork'), s: child?.s, from: node.id, to: it.grid ? null : child.id });
  }
}

/** 一個家族可能是好幾棵樹（森林），並排。回傳卡片座標、格子群組、連接線與整體寬高。 */
export function layoutForest(trees, S = SIZES) {
  const out = { cards: [], groups: [], edges: [], width: 0, height: 0 };
  let x = 0;
  const measured = trees.map(t => measure(t, S));
  for (const m of measured) {
    place(m, x, 0, S, out);
    out.height = Math.max(out.height, m.h);
    x += m.w + S.TREE_GAP;
  }
  out.width = Math.max(0, x - S.TREE_GAP);
  // 根卡片標記：沒有 k（沒有父）的就是根
  for (const c of out.cards) if (!c.n.k) c.root = true;
  return out;
}

/** 連接線走直角肘：先下、再橫、再下。同一直線時就是一條直線。水平版（e.h）：先右、再直、再右。 */
export function edgePath(e, S = SIZES) {
  if (e.h) {
    const mx = e.x1 + S.GY / 2;
    if (Math.abs(e.y1 - e.y2) < 0.5) return `M${e.x1} ${e.y1}H${e.x2}`;
    return `M${e.x1} ${e.y1}H${mx}V${e.y2}H${e.x2}`;
  }
  const my = e.y1 + S.GY / 2;
  if (Math.abs(e.x1 - e.x2) < 0.5) return `M${e.x1} ${e.y1}V${e.y2}`;
  return `M${e.x1} ${e.y1}V${my}H${e.x2}V${e.y2}`;
}

/** 水平版（2026-09-20，靈魂魚頁哲宇：「這邊排水平的比較好」）：同一套量法，把「代」的軸從往下換成往右。
 *  做法是把直版的座標轉置：直版一張卡的橫向步距 CW+GX（150）剛好等於卡高 CH（150），所以同代卡片在轉置後上下疊得剛好；
 *  代與代的步距 CH+GY（202）轉成橫向，卡寬 132 之外留 70px 給連接線。連接線改從父卡右緣中點接到子卡左緣中點。 */
export function transposeLayout(L, S = SIZES) {
  const cards = L.cards.map(c => ({ ...c, x: c.y, y: c.x }));
  const groups = L.groups.map(g => ({ ...g, x: g.y, y: g.x, w: g.h, h: g.w }));
  const byId = Object.fromEntries(cards.map(c => [c.n.id, c]));
  const edges = L.edges.map(e => {
    const from = byId[e.from];
    const x1 = from ? from.x + S.CW : e.y1, y1 = from ? from.y + S.CH / 2 : e.x1;
    if (e.to && byId[e.to]) { const to = byId[e.to]; return { ...e, h: true, x1, y1, x2: to.x, y2: to.y + S.CH / 2 }; }
    const g = groups.find(g => g.parent === e.from);
    return { ...e, h: true, x1, y1, x2: g ? g.x : e.y2, y2: g ? g.y + g.h / 2 : e.x2 };
  });
  // 尺寸從轉置後的卡片與格子算：直版一列的「寬」是 CW（132）而卡高是 CH（150），直接對調會少 18px 把日期那行切掉
  const width = Math.max(0, ...cards.map(c => c.x + S.CW), ...groups.map(g => g.x + g.w));
  const height = Math.max(0, ...cards.map(c => c.y + S.CH), ...groups.map(g => g.y + g.h));
  return { cards, groups, edges, width, height };
}
